import ChatGPTService from '@src/services/ChatGPTService';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import DeleteIcon from '../../svgs/DeleteIcon';
import PencilIcon from '../../svgs/PencilIcon';
import Accordion from './Accordion';

import type { BrandingAudience } from '@src/types.ts/story';

// Styled Components
const AudiencesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AudienceItemContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
`;

const AudienceContent = styled.div`
  flex: 1;
`;

const AudienceLabel = styled.h4`
  color: var(--foreground);
  font-size: 16px;
  font-weight: var(--font-weight-semibold);
  margin: 0 0 8px 0;
`;

const AudienceDescriptionWrapper = styled.div`
  position: relative;

  &:hover > div {
    visibility: visible;
    opacity: 1;
  }
`;

const AudienceDescription = styled.p`
  white-space: pre-wrap;
  color: var(--text-muted);
  font-size: var(--text-sm);
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AudienceTooltip = styled.div`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  z-index: 100005;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 16px;
  width: 600px;
  max-width: 90vw;
  box-shadow: var(--elevation-md);
  transition:
    opacity var(--transition-normal),
    visibility var(--transition-normal);
`;

const AudienceTooltipTitle = styled.h4`
  color: var(--foreground);
  font-size: 16px;
  font-weight: var(--font-weight-semibold);
  margin: 0 0 12px 0;
`;

const AudienceTooltipDescription = styled.p`
  white-space: pre-wrap;
  color: var(--text-muted);
  font-size: var(--text-sm);
  line-height: 1.5;
  margin: 0;
`;

const AudienceActions = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity var(--transition-normal);

  ${AudienceItemContainer}:hover & {
    opacity: 1;
  }
`;

const IconButton = styled.button<{ isEdit?: boolean; isDelete?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-normal);
  opacity: ${props => (props.disabled ? 0.5 : 1)};

  &:hover:not(:disabled) {
    svg path {
      transition: all var(--transition-normal);
      stroke: ${props =>
        props.isEdit
          ? 'var(--accent)'
          : props.isDelete
            ? 'var(--destructive)'
            : 'var(--text-muted)'};
    }
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const AddButton = styled.button<{ isSaving?: boolean }>`
  align-self: flex-end;
  display: block;
  height: 48px;
  padding: 12px 24px;
  border-radius: 8px;
  background: var(--primary);
  color: var(--background);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);
  border: none;
  transition: var(--transition-normal);
  opacity: ${({ isSaving }) => (isSaving ? 0.6 : 1)};
  cursor: ${({ isSaving }) => (isSaving ? 'not-allowed' : 'pointer')};
  &:hover:not(:disabled) {
    filter: brightness(1.2) saturate(1.2);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px;
  border: 1px solid var(--border);
  border-radius: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-regular);
  line-height: 1.5;
  outline: none;
  transition: border-color var(--transition-normal);
  font-weight: var(--font-weight-semibold);
  border-bottom: 1px solid var(--border);

  &::placeholder {
    color: var(--border);
  }

  &:focus {
    border-color: var(--text-secondary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  min-height: 24px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-regular);
  line-height: 1.5;
  outline: none;
  resize: none;
  overflow: hidden;
  transition: border-color var(--transition-normal);
  border-bottom: 1px solid var(--border);

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    border-color: var(--text-secondary);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
`;

const DescriptionLabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const GenerateButton = styled.button`
  background: none;
  border: 1px solid var(--primary);
  color: var(--primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all var(--transition-normal);

  &:hover:not(:disabled) {
    background: var(--primary);
    color: var(--background);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RegenerateContainer = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: var(--primary-tint-light);
  border-radius: 4px;
`;

const RegenerateInput = styled.textarea`
  width: 100%;
  min-height: 60px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 8px;
  color: var(--text-secondary);
  font-size: 13px;
  resize: vertical;
  font-family: var(--font-primary);

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RegenerateActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  justify-content: flex-end;
`;

const RegenerateButton = styled.button`
  background: var(--primary);
  color: var(--background);
  border: none;
  padding: 6px 16px;
  border-radius: 4px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-normal);

  &:hover:not(:disabled) {
    filter: brightness(1.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelRegenerateButton = styled.button`
  background: transparent;
  color: var(--text-muted);
  border: 1px solid var(--border);
  padding: 6px 16px;
  border-radius: 4px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all var(--transition-normal);

  &:hover:not(:disabled) {
    border-color: var(--text-muted);
    color: var(--text-secondary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyStateContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const EmptyStateText = styled.p`
  color: var(--text-muted);
  font-size: var(--text-sm);
  line-height: 1.5;
  margin-bottom: 24px;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const FormButton = styled.button<{ $outlined?: boolean }>`
  display: block;
  height: 48px;
  padding: 12px 24px;
  border-radius: 8px;
  background: ${props => (props.$outlined ? 'transparent' : 'var(--primary)')};
  color: ${props => (props.$outlined ? 'var(--primary)' : 'var(--background)')};
  border: ${props => (props.$outlined ? '1px solid var(--primary)' : 'none')};
  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  transition: var(--transition-normal);
  opacity: ${props => (props.disabled ? 0.6 : 1)};

  &:disabled {
    color: var(--text-muted);
    border: 1px solid var(--text-muted);
    background: transparent;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    filter: brightness(1.2) saturate(1.2);
  }
`;

// Component definitions
interface AudienceFormProps {
  audience: BrandingAudience;
  onSave: (audience: BrandingAudience) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function AudienceForm({
  audience,
  onSave,
  onCancel,
  isSaving,
}: AudienceFormProps) {
  const isEditMode = !!audience.id && !audience.id.includes('temp-');
  const [description, setDescription] = useState(audience.finalPrompt);
  const [label, setLabel] = useState(audience.label);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [showRegenerateInput, setShowRegenerateInput] = useState(false);
  const [regenerateInstructions, setRegenerateInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editedTitle, setEditedTitle] = useState(audience.label);
  const [editedDescription, setEditedDescription] = useState(
    audience.finalPrompt,
  );

  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const chatGPTServiceRef = useRef<ChatGPTService | null>(null);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Initialize ChatGPTService when component mounts
  useEffect(() => {
    if (videoCreator && datoClientStore?.aiPromptRepository) {
      chatGPTServiceRef.current = new ChatGPTService(
        videoCreator,
        datoClientStore.aiPromptRepository,
      );
    }
  }, [videoCreator, datoClientStore]);

  // Update edited values when audience changes (e.g., when entering edit mode)
  useEffect(() => {
    setEditedTitle(audience.label);
    setEditedDescription(audience.finalPrompt);
  }, [audience.id, audience.label, audience.finalPrompt]);

  // Auto-resize textarea to fit content
  const autoResizeTextarea = (textarea: HTMLTextAreaElement | null) => {
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    autoResizeTextarea(descriptionTextareaRef.current);
  }, [editedDescription, isEditMode]);

  const handleEnhanceWithAI = async () => {
    // Need either title or description to enhance
    const inputText = description.trim() || label.trim();
    if (!inputText || !chatGPTServiceRef.current) return;

    setIsEnhancing(true);
    setError(null);

    try {
      const result = await chatGPTServiceRef.current.generateAudience({
        audienceName: inputText,
        existingAudience: undefined, // Force title generation
      });

      if (typeof result === 'object' && result.title && result.description) {
        setLabel(result.title);
        onSave({
          ...audience,
          label: result.title,
          prePrompt: inputText, // Save original input
          finalPrompt: result.description, // Save AI-enhanced description
        });
      } else {
        const desc = typeof result === 'string' ? result : result.description;
        onSave({
          ...audience,
          label: label || 'New Audience',
          prePrompt: inputText,
          finalPrompt: desc,
        });
      }
    } catch (err) {
      console.error('Error enhancing audience with AI:', err);
      setError('Failed to enhance audience. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleRegenerateDescription = async () => {
    if (!regenerateInstructions.trim() || !chatGPTServiceRef.current) return;

    setIsEnhancing(true);
    setError(null);

    try {
      const result = await chatGPTServiceRef.current.generateAudience({
        audienceName: label,
        customInstructions: regenerateInstructions,
        existingAudience: audience.finalPrompt, // Pass existing for regeneration (no title)
      });

      const newDescription =
        typeof result === 'string' ? result : result.description;

      onSave({
        ...audience,
        finalPrompt: newDescription, // Only update finalPrompt, keep title and prePrompt
      });

      setShowRegenerateInput(false);
      setRegenerateInstructions('');
    } catch (err) {
      console.error('Error regenerating audience description:', err);
      setError('Failed to regenerate description. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleSaveAudience = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate at least one field is filled
    if (!label.trim() && !description.trim()) {
      setError('Please enter either a title or a description');
      return;
    }

    // In edit mode, use edited values
    if (isEditMode) {
      onSave({
        ...audience,
        label: editedTitle.trim() || audience.label,
        finalPrompt: editedDescription.trim() || audience.finalPrompt,
        prePrompt: audience.prePrompt, // Keep original prePrompt
      });
      return;
    }

    // For new audiences, save with whatever is provided
    onSave({
      ...audience,
      label: label.trim() || 'New Audience',
      prePrompt: description.trim() || label.trim(), // Use title as prePrompt if no description
      finalPrompt: description.trim() || label.trim(), // Same as prePrompt when not enhanced
    });
  };

  return (
    <FormContainer onSubmit={handleSaveAudience}>
      <FormFields>
        {/* Edit mode - show input fields directly */}
        {isEditMode && (
          <>
            <div>
              <div
                style={{
                  color: 'var(--text-muted)',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                }}
              >
                Audience Title
              </div>
              <FormInput
                type="text"
                value={editedTitle}
                onChange={e => setEditedTitle(e.target.value)}
                placeholder="Enter a title for this audience"
                disabled={isSaving || isEnhancing}
              />
            </div>

            <div>
              <DescriptionLabelContainer>
                <span
                  style={{
                    color: 'var(--text-muted)',
                    fontSize: '12px',
                    textTransform: 'uppercase',
                  }}
                >
                  Description
                </span>
                {!showRegenerateInput && (
                  <GenerateButton
                    type="button"
                    onClick={() => setShowRegenerateInput(true)}
                    disabled={isEnhancing}
                  >
                    Regenerate
                  </GenerateButton>
                )}
              </DescriptionLabelContainer>
              <FormTextarea
                ref={descriptionTextareaRef}
                value={editedDescription}
                onChange={e => {
                  setEditedDescription(e.target.value);
                  autoResizeTextarea(e.target);
                }}
                rows={1}
                placeholder="Describe your audience"
                disabled={isSaving || isEnhancing || showRegenerateInput}
              />

              {showRegenerateInput && (
                <RegenerateContainer>
                  <RegenerateInput
                    value={regenerateInstructions}
                    onChange={e => setRegenerateInstructions(e.target.value)}
                    placeholder="How would you like to improve the audience description? (e.g., 'More demographic details', 'Include psychographics', 'Focus on pain points')"
                    disabled={isEnhancing}
                  />
                  <RegenerateActions>
                    <RegenerateButton
                      type="button"
                      onClick={handleRegenerateDescription}
                      disabled={isEnhancing || !regenerateInstructions.trim()}
                    >
                      {isEnhancing ? 'Regenerating...' : 'Regenerate'}
                    </RegenerateButton>
                    <CancelRegenerateButton
                      type="button"
                      onClick={() => {
                        setShowRegenerateInput(false);
                        setRegenerateInstructions('');
                      }}
                      disabled={isEnhancing}
                    >
                      Cancel
                    </CancelRegenerateButton>
                  </RegenerateActions>
                </RegenerateContainer>
              )}

              {audience.prePrompt &&
                audience.prePrompt !== audience.finalPrompt && (
                  <div style={{ marginTop: '16px' }}>
                    <span
                      style={{
                        color: 'var(--text-muted)',
                        fontSize: '12px',
                        textTransform: 'uppercase',
                      }}
                    >
                      Original Description
                    </span>
                    <p
                      style={{
                        color: 'var(--text-muted)',
                        fontSize: '13px',
                        lineHeight: '1.5',
                        marginTop: '4px',
                      }}
                    >
                      {audience.prePrompt}
                    </p>
                  </div>
                )}
            </div>
          </>
        )}

        {/* New audience creation mode */}
        {!isEditMode && (
          <>
            <FormInput
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Enter a title for this audience (optional)"
              disabled={isSaving || isEnhancing}
              autoFocus
            />

            <FormTextarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={4}
              placeholder="Describe your target audience. For example: 'Younger investors, professionals, and entrepreneurs beginning to build wealth. They are drawn to digital-first solutions, value-aligned investing, and advisors who understand their unique life stage and financial priorities.'"
              disabled={isSaving || isEnhancing}
            />
          </>
        )}

        {error && (
          <div
            style={{
              color: 'var(--destructive)',
              fontSize: '12px',
              marginTop: '8px',
            }}
          >
            {error}
          </div>
        )}
      </FormFields>

      <FormActions>
        <FormButton
          type="button"
          onClick={onCancel}
          disabled={isSaving || isEnhancing}
          $outlined
        >
          Cancel
        </FormButton>

        {!isEditMode && (
          <>
            <FormButton
              type="submit"
              disabled={
                isSaving ||
                isEnhancing ||
                (!label.trim() && !description.trim())
              }
            >
              {isSaving ? 'Saving...' : 'Save Audience'}
            </FormButton>

            <FormButton
              type="button"
              onClick={handleEnhanceWithAI}
              disabled={
                isSaving ||
                isEnhancing ||
                (!label.trim() && !description.trim())
              }
            >
              {isEnhancing ? 'Enhancing...' : 'Enhance with AI'}
            </FormButton>
          </>
        )}

        {isEditMode && (
          <FormButton type="submit" disabled={isSaving || isEnhancing}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </FormButton>
        )}
      </FormActions>
    </FormContainer>
  );
}

interface AudienceItemProps {
  audience: BrandingAudience;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (audience: BrandingAudience) => void;
  onDelete: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

function AudienceItem({
  audience,
  isEditing,
  onEdit,
  onSave,
  onDelete,
  onCancel,
  isSaving,
}: AudienceItemProps) {
  if (isEditing) {
    return (
      <AudienceForm
        audience={audience}
        onSave={onSave}
        onCancel={onCancel}
        isSaving={isSaving}
      />
    );
  }

  return (
    <AudienceItemContainer>
      <AudienceContent>
        <AudienceLabel>{audience.label}</AudienceLabel>
        <AudienceDescriptionWrapper>
          <AudienceDescription>{audience.finalPrompt}</AudienceDescription>
          <AudienceTooltip>
            <AudienceTooltipTitle>{audience.label}</AudienceTooltipTitle>
            <AudienceTooltipDescription>
              {audience.finalPrompt}
            </AudienceTooltipDescription>
          </AudienceTooltip>
        </AudienceDescriptionWrapper>
      </AudienceContent>
      <AudienceActions>
        <IconButton onClick={onEdit} disabled={isSaving} isEdit>
          <PencilIcon width="22" height="22" strokeColor="var(--text-muted)" />
        </IconButton>
        <IconButton onClick={onDelete} disabled={isSaving} isDelete>
          <DeleteIcon width="22" height="22" strokeColor="var(--text-muted)" />
        </IconButton>
      </AudienceActions>
    </AudienceItemContainer>
  );
}

const BrandKitAudiences = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();

  const branding = videoCreator.organization?.branding;
  const [audiences, setAudiences] = useState<BrandingAudience[]>(
    branding?.audiences || [],
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [editingAudience, setEditingAudience] =
    useState<BrandingAudience | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isGeneratingMultiple, setIsGeneratingMultiple] = useState(false);
  const [isGeneratingNew, setIsGeneratingNew] = useState(false);
  const chatGPTServiceRef = useRef<ChatGPTService | null>(null);

  // Initialize ChatGPTService when component mounts
  useEffect(() => {
    if (videoCreator && datoClientStore?.aiPromptRepository) {
      chatGPTServiceRef.current = new ChatGPTService(
        videoCreator,
        datoClientStore.aiPromptRepository,
      );
    }
  }, [videoCreator, datoClientStore]);

  useEffect(() => {
    if (branding?.audiences) setAudiences(branding.audiences);
  }, [branding?.audiences]);

  const handleCreateAudience = async (
    audienceData: Omit<BrandingAudience, 'id'>,
  ) => {
    const itemType =
      await datoClientStore.datoClient.itemTypes.find('audience');
    const createdAudience = await datoClientStore.datoClient.items.create({
      item_type: { type: 'item_type', id: itemType.id },
      label: audienceData.label,
      pre_prompt: audienceData.prePrompt,
      final_prompt: audienceData.finalPrompt,
    });
    await datoClientStore.datoClient.items.publish(createdAudience.id);
    return createdAudience;
  };

  const handleUpdateAudience = async (audience: BrandingAudience) => {
    await datoClientStore.datoClient.items.update(audience.id, {
      label: audience.label,
      pre_prompt: audience.prePrompt,
      final_prompt: audience.finalPrompt,
    });
    await datoClientStore.datoClient.items.publish(audience.id);
  };

  const handleDeleteAudience = async (audienceId: string) => {
    await datoClientStore.datoClient.items.destroy(audienceId);
  };

  const updateBrandingWithAudiences = async (
    updatedAudiences: BrandingAudience[],
  ) => {
    const { organization } = videoCreator;
    if (!organization?.branding) return;

    organization.branding = {
      ...organization.branding,
      audiences: updatedAudiences,
    };
    await datoClientStore.albumRepository?.update(organization);
  };

  const handleAddAudience = () => {
    const newAudience: BrandingAudience = {
      id: `temp-${Date.now()}`,
      label: '',
      prePrompt: '',
      finalPrompt: '',
    };
    setEditingAudience(newAudience);
    setIsAddingNew(true);
  };

  // Remove auto-open form to allow showing Generate 4 button
  // useEffect(() => {
  //   if (audiences.length === 0 && !editingAudience && !isAddingNew) {
  //     handleAddAudience();
  //   }
  // }, [audiences.length, editingAudience, isAddingNew]);

  const handleSaveAudience = async (audience: BrandingAudience) => {
    setIsSaving(true);
    setIsSaved(false);

    try {
      if (isAddingNew) {
        // Store original state for potential reversion
        const originalAudiences = audiences;

        // Immediately update UI state for responsive feedback
        const tempAudience: BrandingAudience = {
          ...audience,
          id: audience.id, // Temp ID
        };
        const optimisticAudiences = [...audiences, tempAudience];

        // Update UI immediately
        setEditingAudience(null);
        setIsAddingNew(false);
        setAudiences(optimisticAudiences);

        // Create new audience record in background
        const createdAudience = await handleCreateAudience({
          label: audience.label,
          prePrompt: audience.prePrompt,
          finalPrompt: audience.finalPrompt,
        });

        // Update with real ID and sync with branding
        const newAudience: BrandingAudience = {
          id: createdAudience.id,
          label: audience.label,
          prePrompt: audience.prePrompt,
          finalPrompt: audience.finalPrompt,
        };
        const updatedAudiences = [...originalAudiences, newAudience];

        // Update branding with new audience reference
        await updateBrandingWithAudiences(updatedAudiences);

        // Update with final data
        setAudiences(updatedAudiences);
      } else {
        // For edits, update UI immediately
        const updatedAudiences = audiences.map(a =>
          a.id === audience.id ? audience : a,
        );
        setEditingAudience(null);
        setAudiences(updatedAudiences);

        // Update existing audience record in background
        await handleUpdateAudience(audience);

        // Update branding reference when editing
        await updateBrandingWithAudiences(updatedAudiences);
      }

      setIsSaving(false);
      setIsSaved(true);
    } catch (error) {
      console.error('Error saving audience:', error);
      setIsSaving(false);
      videoCreator.showToast({
        state: 'error',
        message: 'Failed to save audience. Please try again.',
      });

      // Revert optimistic update on error
      if (isAddingNew) {
        const originalAudiences = audiences; // Restore to pre-optimistic state
        setAudiences(originalAudiences);
        setEditingAudience(audience); // Show form again
        setIsAddingNew(true);
      }
    }
  };

  const handleRemoveAudience = async (audienceId: string) => {
    setIsSaving(true);
    setIsSaved(false);

    // Store original state for potential restoration
    const originalAudiences = audiences;
    const audienceToDelete = audiences.find(a => a.id === audienceId);
    if (!audienceToDelete) return;

    // Immediately remove from UI for responsive feedback
    const optimisticAudiences = audiences.filter(a => a.id !== audienceId);
    setAudiences(optimisticAudiences);

    try {
      // Delete the audience record in background
      await handleDeleteAudience(audienceId);

      // Update branding to remove audience reference
      await updateBrandingWithAudiences(optimisticAudiences);

      setIsSaving(false);
      setIsSaved(true);
    } catch (error) {
      console.error('Error deleting audience:', error);
      setIsSaving(false);
      videoCreator.showToast({
        state: 'error',
        message: 'Failed to delete audience. Please try again.',
      });

      // Restore to original state before optimistic update
      setAudiences(originalAudiences);
    }
  };

  const handleCancelEdit = () => {
    setEditingAudience(null);
    setIsAddingNew(false);
  };

  const handleGenerateMultipleAudiences = async () => {
    if (!chatGPTServiceRef.current) return;

    setIsGeneratingMultiple(true);
    setIsSaved(false);

    try {
      const generatedAudiences =
        await chatGPTServiceRef.current.generateMultipleAudiences(4);

      // Create all audiences in parallel
      const createdAudiences = await Promise.all(
        generatedAudiences.map(async audienceData => {
          const created = await handleCreateAudience({
            label: audienceData.title,
            prePrompt: audienceData.title, // Store the title as the original input
            finalPrompt: audienceData.description,
          });

          return {
            id: created.id,
            label: audienceData.title,
            prePrompt: audienceData.title,
            finalPrompt: audienceData.description,
          } as BrandingAudience;
        }),
      );

      // Update state and branding
      const updatedAudiences = [...audiences, ...createdAudiences];
      await updateBrandingWithAudiences(updatedAudiences);
      setAudiences(updatedAudiences);
      setIsSaved(true);
    } catch (error) {
      console.error('Error generating multiple audiences:', error);
      videoCreator.showToast({
        state: 'error',
        message: 'Failed to generate audiences. Please try again.',
      });
    } finally {
      setIsGeneratingMultiple(false);
    }
  };

  const handleGenerateNewAudience = async () => {
    if (!chatGPTServiceRef.current) return;

    setIsGeneratingNew(true);
    setIsSaved(false);

    try {
      // Get existing audience context
      const existingContext =
        audiences.length > 0
          ? `\n\nExisting audiences to differentiate from:\n${audiences
              .map(a => `- ${a.label}: ${a.finalPrompt}`)
              .join('\n')}`
          : '';

      const result = await chatGPTServiceRef.current.generateAudience({
        customInstructions: `Generate a new audience that is distinct from the existing ones.${existingContext}`,
      });

      if (typeof result === 'object' && result.title && result.description) {
        // Create the new audience
        const created = await handleCreateAudience({
          label: result.title,
          prePrompt: result.title,
          finalPrompt: result.description,
        });

        const newAudience: BrandingAudience = {
          id: created.id,
          label: result.title,
          prePrompt: result.title,
          finalPrompt: result.description,
        };

        // Update state and branding
        const updatedAudiences = [...audiences, newAudience];
        await updateBrandingWithAudiences(updatedAudiences);
        setAudiences(updatedAudiences);
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Error generating new audience:', error);
      videoCreator.showToast({
        state: 'error',
        message: 'Failed to generate audience. Please try again.',
      });
    } finally {
      setIsGeneratingNew(false);
    }
  };

  return (
    <Accordion
      id="brand-audiences"
      title="Audiences"
      isSaving={isSaving}
      isSaved={isSaved}
    >
      <AudiencesContainer>
        {audiences.length === 0 && !editingAudience && (
          <EmptyStateContainer>
            <EmptyStateText>
              No audiences defined yet. Generate AI-powered audiences or add
              your own.
            </EmptyStateText>
          </EmptyStateContainer>
        )}

        {audiences.map(audience => (
          <AudienceItem
            key={audience.id}
            audience={audience}
            isEditing={editingAudience?.id === audience.id}
            onEdit={() => setEditingAudience(audience)}
            onSave={handleSaveAudience}
            onDelete={() => handleRemoveAudience(audience.id)}
            onCancel={handleCancelEdit}
            isSaving={isSaving}
          />
        ))}

        {!!branding && editingAudience && isAddingNew && (
          <AudienceForm
            audience={editingAudience}
            onSave={handleSaveAudience}
            onCancel={handleCancelEdit}
            isSaving={isSaving}
          />
        )}

        {!editingAudience && (
          <ButtonContainer>
            {audiences.length === 0 && (
              <FormButton
                type="button"
                onClick={handleGenerateMultipleAudiences}
                disabled={isSaving || isGeneratingMultiple}
              >
                {isGeneratingMultiple
                  ? 'Generating...'
                  : 'Generate 4 Audiences'}
              </FormButton>
            )}
            {audiences.length > 0 && (
              <FormButton
                type="button"
                onClick={handleGenerateNewAudience}
                disabled={isSaving || isGeneratingNew}
                $outlined
              >
                {isGeneratingNew ? 'Generating...' : 'Generate New'}
              </FormButton>
            )}
            <AddButton
              onClick={handleAddAudience}
              disabled={isSaving || isGeneratingMultiple || isGeneratingNew}
            >
              Add New
            </AddButton>
          </ButtonContainer>
        )}
      </AudiencesContainer>
    </Accordion>
  );
});

export default BrandKitAudiences;
